import { Box, Link } from "@mui/material";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { useCallback, useState } from "react";
import {
  FunctionField,
  ReferenceField,
  Show,
  SimpleShowLayout,
  TextField,
  useRecordContext,
  useTheme,
} from "react-admin";
import {
  FlexBetween,
  FormGrid,
  HeaderSubMetaLabel,
  MapButton,
  MapHeaderContainer,
  OutlinedButton,
  Paragraph,
  ShowItemCOntainer,
  ShowLabel,
  ThreeColGrid,
  WorkspaceEditButton,
  WorkSpaceTitleRow,
} from "../../styles/commonStyles";
import { ImagePlaceholderIcon, LocationIcon } from "../Svg";
import { colors } from "../../styles/colors";
import { formatDateTimeWithSeconds } from "../../utils/DateTimeUtils";

const containerStyle = {
  width: "100%",
  height: "50vh",
};
const HouseMetaShow = () => {
  const record = useRecordContext();
  const [view, setView] = useState(false);
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY as string,
  });

  const [theme] = useTheme();

  const currentTheme = theme.palette?.mode;

  const center = {
    lat: record?.lat || 0,
    lng: record?.long || 0,
  };

  function getUrlParams() {
    const params = new URLSearchParams();
    if (record?.long !== null) params.append("long", String(record.long));
    if (record?.lat !== null) params.append("lat", String(record.lat));
    if (record?.id !== null) params.append("houseId", String(record.id));

    return params.toString();
  }

  const onUnmount = useCallback(function callback() {}, []);

  const CustomShowActions = () => null;

  return (
    <Show actions={<CustomShowActions />}>
      <SimpleShowLayout
        sx={{
          padding: "0px",
        }}
      >
        {/*  */}
        <FlexBetween>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {/* <span># </span> */}
            <HeaderSubMetaLabel># HSE-ID:</HeaderSubMetaLabel>
            <TextField source="id" />
          </div>

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: "16px" }}
          >
            <Link
              href={`#/HouseBanners?${getUrlParams()}`}
              target={"_blank"}
              style={{ textDecoration: "none" }}
            >
              <OutlinedButton variant={"contained"} size="small">
                <ImagePlaceholderIcon color={colors.primary} size={16} />
                See Banners
              </OutlinedButton>
            </Link>

            <Link href={`#/houses/${record?.id}`}>
              <WorkspaceEditButton
                variant={"contained"}
                size="small"
                label="Edit Details"
              />
            </Link>
          </Box>
        </FlexBetween>

        <FormGrid>
          <ShowItemCOntainer>
            <ShowLabel>Full Address</ShowLabel>
            <FunctionField
              emptyText=""
              label="Address"
              render={(record: any) =>
                record?.fullAddress || "-"
              }
            />
          </ShowItemCOntainer>
          <ShowItemCOntainer>
            <ShowLabel>Map Location</ShowLabel>
            <TextField label="Map Location" source="city" />
          </ShowItemCOntainer>
        </FormGrid>
        <ThreeColGrid>
          <ShowItemCOntainer>
            <ShowLabel>City</ShowLabel>
            <ReferenceField
              source="cityId"
              link="show"
              reference="Cities"
              emptyText="Not Assigned"
            >
              <TextField source="name" />
            </ReferenceField>
          </ShowItemCOntainer>

          <ShowItemCOntainer>
            <ShowLabel>Area</ShowLabel>
            <ReferenceField
              source="areaId"
              link="show"
              reference="Areas"
              emptyText="Not Assigned"
            >
              <TextField source="name" />
            </ReferenceField>
          </ShowItemCOntainer>

          <ShowItemCOntainer>
            <ShowLabel>Locality</ShowLabel>
            <ReferenceField
              source="localityId"
              link="show"
              reference="Localities"
              emptyText="Not Assigned"
            >
              <TextField source="name" />
            </ReferenceField>
          </ShowItemCOntainer>
        </ThreeColGrid>

        <ThreeColGrid>
          <ShowItemCOntainer>
            <ShowLabel>Landmark / Society</ShowLabel>
            <TextField source="locality" emptyText="Not Assigned" />
          </ShowItemCOntainer>

          <ShowItemCOntainer>
            <ShowLabel>Sector</ShowLabel>
            <TextField source="sector" emptyText="Not Assigned" />
          </ShowItemCOntainer>

          <ShowItemCOntainer>
            <ShowLabel>Lat</ShowLabel>
            <TextField source="lat" emptyText="Not Assigned" />
          </ShowItemCOntainer>
        </ThreeColGrid>

        <ThreeColGrid>
          <ShowItemCOntainer>
            <ShowLabel>Long</ShowLabel>
            <TextField source="long" emptyText="Not Assigned" />
          </ShowItemCOntainer>

          <ShowItemCOntainer>
            <ShowLabel>Other SLC Name</ShowLabel>
            <TextField source="otherSlcName" emptyText="Not Assigned" />
          </ShowItemCOntainer>

          <ShowItemCOntainer>
            <ShowLabel>Created At</ShowLabel>
            <FunctionField
              render={(record: any) =>
                record?.createdAt
                  ? formatDateTimeWithSeconds(record.createdAt)
                  : "-"
              }
            />
          </ShowItemCOntainer>
        </ThreeColGrid>

        <ThreeColGrid>
          <ShowItemCOntainer>
            <ShowLabel>Updated At</ShowLabel>
            <FunctionField
              render={(record: any) =>
                record?.updatedAt
                  ? formatDateTimeWithSeconds(record.updatedAt)
                  : "-"
              }
            />
          </ShowItemCOntainer>
        </ThreeColGrid>
      </SimpleShowLayout>

      <Box
        sx={{
          padding: "0",
          marginTop: "20px",

          "& .MuiStack-root > :not(style) + :not(style)": {
            marginTop: 0,
          },
        }}
      >
        <MapHeaderContainer>
          <div>
            <WorkSpaceTitleRow>
              <LocationIcon />
              <Paragraph>Map Location</Paragraph>
            </WorkSpaceTitleRow>
          </div>

          <div>
            <MapButton
              onClick={() => {
                setView(!view);
              }}
            >
              {!view ? "View on Map" : "Hide House"}
            </MapButton>
          </div>
        </MapHeaderContainer>

        {isLoaded && view && record?.lat && record?.long ? (
          <div>
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={center}
              zoom={20}
              onUnmount={onUnmount}
            >
              {record?.lat && record?.long && (
                <Marker
                  position={{
                    lat: record?.lat,
                    lng: record?.long,
                  }}
                  icon={{
                    url: "/house.png",
                    scaledSize: new window.google.maps.Size(80, 80),
                  }}
                />
              )}
            </GoogleMap>
          </div>
        ) : (
          <Box
            sx={{
              height: "300px",
              backgroundImage:
                currentTheme === "dark"
                  ? "url(/map-placeholder-dark-bg.webp)"
                  : "url('/map-placeholder.jpg')",
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "blur(1px)",
              borderRadius: "0px 0px 12px 12px",
              position: "relative",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255,255,255,0.4)",
              }}
            >
              <Paragraph>Click ‘View on Map’ to load</Paragraph>
            </Box>
          </Box>
        )}
      </Box>
    </Show>
  );
};

export default HouseMetaShow;
